import { useState, useRef, useMemo, useEffect } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { t } from '../i18n/translations'
import { useAuth } from '../context/AuthContext'
import { useDevMode } from '../context/DevModeContext'
import AudioPlayer from './AudioPlayer'
import { detectRole, RoleBadge } from './RoleIcon'
import { shareAudioWithText, shareNoteFor, downloadBlob, audioFileName } from '../lib/shareAudio'
import wordTimestamps from '../data/word-timestamps.json'
import { isFreeLesson } from '../lib/access'


// A token is a real word if it contains at least one Ethiopic letter (U+1200-U+137F)
const isGeezWord = (token) => /[\u1200-\u137F]/.test(token)

// Letters only — Ethiopic punctuation (፡ ። ፣, U+1360+) is not a word
const GEEZ_LETTER = /[ሀ-ፚ]/
const LATIN_LETTER = /[A-Za-z]/

// Indices of the tokens that carry real words, so the Ge'ez and its Latin
// transliteration can be lined up one-to-one. Skips bare punctuation, a leading
// role marker ("behibret፡"), and repeat-count asides — "(፫-፳)" as its own token
// or glued onto a word — which appear in one script but not always the other.
function realWordIndices(tokens, letter) {
  const indices = []
  let depth = 0
  tokens.forEach((token, i) => {
    let core = ''
    for (const ch of token) {
      if (ch === '(' || ch === '[') { depth++; continue }
      if (ch === ')' || ch === ']') { if (depth > 0) depth--; continue }
      if (depth === 0) core += ch
    }
    if (!letter.test(core)) return
    if (i === 0 && /[፡:]$/.test(token)) return
    indices.push(i)
  })
  return indices
}



export default function LiturgySection({ section, categoryId, sequenceNumber, isSelected, onSelect, onLockedClick, lessonProgress, onProgress, lessonIndex }) {
  const { translationField, textVisibility } = useLanguage()
  const { user, isPremium } = useAuth()
  const { devMode } = useDevMode()
  const [copyToast, setCopyToast] = useState(false)
  const [activeWordIndex, setActiveWordIndex] = useState(-1)
  const [liveTimestamps, setLiveTimestamps] = useState(null)
  const markWordRef = useRef(null) // holds AudioPlayer's markWord function during marking
  const [seekRequest, setSeekRequest] = useState(null) // { time, wordIndex, totalWords, id }
  const [recordingBlobUrl, setRecordingBlobUrl] = useState(null)
  const lessonBlobRef = useRef(null)      // lesson audio, held in memory for sharing
  const [shareNote, setShareNote] = useState('')

  const geezText = section._geeztext || ''
  const middleText = section[translationField] || ''
  const englishText = section._englishtext || ''
  const audioUrl = section.firebase_audio_url || ''
  // Lock lessons beyond the free limit for unregistered users
  const isLocked = !isPremium && !isFreeLesson(lessonIndex)

  // Check if text starts with "+" (indicates a special liturgical marker)
  const isSpecialSection = geezText.startsWith('+')

  // Detect liturgical role from section data, or use inherited role from assignRoles
  const ROLE_LABELS = { priest: 'ካህን', asstpriest: 'ረ.ካህን', deacon: 'ዲያቆን', people: 'ሕዝብ' }
  const detected = detectRole(section)
  const roleInfo = detected || (section._role ? { role: section._role, label: ROLE_LABELS[section._role] || '' } : null)
  const stripRole = (text) => text
    .replace(/^\+\s*/, '')
    .replace(/^(ካህን|ሕዝብ|ዲያቆን|በሕብረት)[፡:]\s*/i, '')
    .replace(/^(Asst?\.?\s*Priest|Asst?\.?\s*Deacon|Priests?|Pries|People|Deacon|Together|[Kk]ahn|Kahin|HIZB|Hizb|Diyaqon)[,:;\-\s]+/i, '')
    .replace(/\s*\(?[(\[]?[፩-፼\d]+(?:-\s*[፩-፼\d]+)*[)\]]\)?\s*$/, '')
  const displayGeez = stripRole(geezText)
  const displayMiddle = middleText && middleText !== '+' ? stripRole(middleText) : ''
  const displayEnglish = englishText && englishText !== '+' ? stripRole(englishText) : ''

  // Check if this lesson has manual word timestamps (structured by category)
  const savedTimestamps = useMemo(() => {
    if (!wordTimestamps.categories) return null
    const cat = wordTimestamps.categories.find(c => c.id === categoryId)
    if (!cat) return null
    const chant = cat.chants.find(ch => ch.id === section.id)
    return chant ? chant.timestamps : null
  }, [categoryId, section.id])
  // Hold the lesson audio in memory once this lesson is open. navigator.share()
  // must be called while the user's tap is still live, so fetching at click
  // time would lose the gesture on iOS — the file has to be ready beforehand.
  // The browser is downloading it for playback anyway.
  useEffect(() => {
    if (!isSelected || !audioUrl || isLocked || lessonBlobRef.current) return
    let cancelled = false
    const controller = new AbortController()
    fetch(audioUrl, { signal: controller.signal })
      .then(r => (r.ok ? r.blob() : null))
      .then(blob => { if (!cancelled && blob) lessonBlobRef.current = blob })
      .catch(() => { /* offline or CORS — share falls back to text */ })
    return () => { cancelled = true; controller.abort() }
  }, [isSelected, audioUrl, isLocked])

  const activeTimestamps = liveTimestamps || savedTimestamps
  const words = useMemo(
    () => displayGeez.split(/\s+/).filter(t => t && isGeezWord(t)),
    [displayGeez]
  )

  // Lesson title: first two Ge'ez words (e.g., "ሕዝብ እምነ")
  const lessonTitle = useMemo(() => words.slice(0, 2).join(' '), [words])

  // What gets shared is what the reader can see: the Show-text toggles decide.
  const shareLabel = sequenceNumber && lessonTitle
    ? `Lesson ${sequenceNumber}. ${lessonTitle}`
    : (lessonTitle || `Kidase ${section.id}`)
  const shareText = [
    shareLabel,
    textVisibility.geez ? displayGeez : '',
    textVisibility.middle ? displayMiddle : '',
    textVisibility.english ? displayEnglish : '',
    '-- Sirate Kidase Tutor',
  ].filter(Boolean).join('\n\n')

  // The Latin transliteration is a one-to-one word mapping of the Ge'ez, so it
  // follows the same highlight. Only sync when the word counts actually agree —
  // a few lessons have a word in one script and not the other, and a drifting
  // highlight is worse than none.
  const isLatinMiddle = translationField === '_latintext'
  const latinTokens = useMemo(
    () => (isLatinMiddle ? displayMiddle.split(/\s+/).filter(Boolean) : []),
    [isLatinMiddle, displayMiddle]
  )
  // latinToGeez[i] = the index into `words` that Latin token i corresponds to,
  // or null when the two scripts don't line up (then the row stays plain text).
  const latinToGeez = useMemo(() => {
    if (!isLatinMiddle || words.length === 0 || latinTokens.length === 0) return null
    const geezReal = realWordIndices(words, GEEZ_LETTER)
    const latinReal = realWordIndices(latinTokens, LATIN_LETTER)
    if (geezReal.length === 0 || geezReal.length !== latinReal.length) return null
    const map = new Array(latinTokens.length).fill(null)
    latinReal.forEach((tokenIndex, pos) => { map[tokenIndex] = geezReal[pos] })
    return map
  }, [isLatinMiddle, words, latinTokens])

  // Click a word in either script: mark it while marking, else seek the audio there
  function handleWordClick(i) {
    if (markWordRef.current) markWordRef.current(i)
    else if (activeTimestamps && activeTimestamps[i] !== undefined) {
      setActiveWordIndex(i)
      setSeekRequest({ time: activeTimestamps[i], id: Date.now() })
    } else if (words.length > 0) {
      setActiveWordIndex(i)
      setSeekRequest({ wordIndex: i, totalWords: words.length, id: Date.now() })
    }
  }

  // Callback from AudioPlayer: receives live marking timestamps + markWord function
  function handleMarkingWords(marks, markFn) {
    setLiveTimestamps(marks)
    markWordRef.current = markFn || null
    if (!marks) setActiveWordIndex(-1)
  }

  // Callback from AudioPlayer: receives currentTime in seconds
  function handleTimeSync(currentTime) {
    if (currentTime < 0) {
      setActiveWordIndex(-1)
      return
    }
    if (!activeTimestamps) {
      // No timestamps — don't override user-clicked word highlight
      return
    }
    // Find the last timestamp <= currentTime
    let idx = -1
    for (let i = activeTimestamps.length - 1; i >= 0; i--) {
      if (currentTime >= activeTimestamps[i]) {
        idx = i
        break
      }
    }
    // Always highlight first word once audio is playing (before first timestamp)
    if (idx === -1 && currentTime >= 0) idx = 0
    setActiveWordIndex(idx)
  }

  function handleClick() {
    if (isLocked) {
      if (onLockedClick) onLockedClick()
    } else {
      onSelect()
    }
  }

  return (
    <div
      className={`liturgy-section ${isLocked ? 'locked' : ''} ${isSpecialSection ? 'special' : ''} ${isSelected && !isLocked ? 'selected' : ''}`}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
    >
      {isLocked && (
        <div className="locked-overlay">
          <span className="lock-icon">&#128274;</span>
          <span className="lock-text">{t(user ? 'subscribeToUnlock' : 'signInToUnlock')}</span>
        </div>
      )}

      <div className={`text-container ${isLocked ? 'blurred' : ''}`}>
        <div className="section-header-row">
          <div className="section-header-left">
            {sequenceNumber && (
              <span className={`section-ref-number ${lessonProgress?.learned ? 'learned' : ''}`}>
                {lessonProgress?.learned && (
                  <svg className="learned-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                )}
                {sequenceNumber}{lessonTitle ? `. ${lessonTitle}` : ''}
              </span>
            )}
            {!sequenceNumber && lessonProgress?.learned && (
              <svg className="learned-check standalone" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            )}
            {roleInfo && !isLocked && (
              <RoleBadge role={roleInfo.role} label={roleInfo.label} />
            )}
          </div>
          {!isLocked && (
            <div className="section-header-right">
              <button
                className="share-btn section-share-btn"
                title="Share lesson"
                onClick={async e => {
                  e.stopPropagation()
                  const ready = lessonBlobRef.current
                  const result = await shareAudioWithText({
                    blob: ready,
                    fileName: audioFileName(shareLabel, ready?.type || "audio/mpeg"),
                    title: shareLabel,
                    text: shareText,
                  })
                  // Don't blame the target app when the audio simply wasn't
                  // downloaded yet.
                  setShareNote(!ready && result.outcome === 'text-only'
                    ? 'Text sent — the lesson audio was still loading. Share again in a moment to include it.'
                    : shareNoteFor(result))
                  if (result.outcome === "text-only" && result.copied) {
                    setCopyToast(true)
                    setTimeout(() => setCopyToast(false), 1500)
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              </button>
              {audioUrl && (
                <button
                  className="share-btn section-share-btn"
                  title="Save lesson audio"
                  onClick={async e => {
                    e.stopPropagation()
                    const name = audioFileName(shareLabel, lessonBlobRef.current?.type || 'audio/mpeg')
                    if (lessonBlobRef.current) {
                      downloadBlob(lessonBlobRef.current, name)
                      setShareNote('Lesson audio saved to your downloads.')
                      return
                    }
                    // Not prefetched yet (lesson not open, or the fetch failed)
                    setShareNote('Preparing the audio…')
                    try {
                      const res = await fetch(audioUrl)
                      const blob = await res.blob()
                      lessonBlobRef.current = blob
                      downloadBlob(blob, audioFileName(shareLabel, blob.type))
                      setShareNote('Lesson audio saved to your downloads.')
                    } catch {
                      setShareNote('Could not download the lesson audio — check your connection.')
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </button>
              )}
              {copyToast && (
                <span className="copy-toast">Copied!</span>
              )}
            </div>
          )}
        </div>
        {textVisibility.geez && (
        <p className="geez-text">
          {isLocked ? geezText.substring(0, 40) + '...' : (
            words.map((word, i) => (
              <span
                key={i}
                className={`text-word ${i === activeWordIndex ? 'word-active' : ''}`}
                onClick={e => {
                  e.stopPropagation()
                  handleWordClick(i)
                }}
              >
                {word}{' '}
              </span>
            ))
          )}
        </p>
        )}
        {textVisibility.middle && displayMiddle && (
          <p className="middle-text">
            {isLocked ? '...' : latinToGeez ? (
              latinTokens.map((word, i) => {
                const geezIndex = latinToGeez[i]
                if (geezIndex === null) return <span key={i}>{word}{' '}</span>
                return (
                  <span
                    key={i}
                    className={`text-word ${geezIndex === activeWordIndex ? 'word-active' : ''}`}
                    onClick={e => {
                      e.stopPropagation()
                      handleWordClick(geezIndex)
                    }}
                  >
                    {word}{' '}
                  </span>
                )
              })
            ) : displayMiddle}
          </p>
        )}
        {shareNote && (
          <p className="section-share-note">{shareNote}</p>
        )}
        {textVisibility.english && displayEnglish && (
          <p className="english-text">
            {isLocked ? '...' : displayEnglish}
          </p>
        )}
      </div>

      {audioUrl && !isLocked && (
        <AudioPlayer
          audioUrl={audioUrl}
          sectionId={section.id}
          categoryId={categoryId}
          lessonLabel={sequenceNumber && lessonTitle ? `Lesson ${sequenceNumber}. ${lessonTitle}` : ''}
          geezText={displayGeez}
          translationText={displayEnglish || displayMiddle}
          onTimeSync={handleTimeSync}
          onMarkingWords={handleMarkingWords}
          onRecordingChange={setRecordingBlobUrl}
          seekRequest={seekRequest}
          savedWordTimestamps={activeTimestamps}
          onProgress={onProgress}
        />
      )}
    </div>
  )
}
